/**
 * UTILITY PER GESTIONE COOKIE
 *
 * Questo modulo fornisce funzioni utility per la gestione dei cookie,
 * principalmente per operazioni di logout e pulizia dello stato.
 */

/**
 * Pulisce tutti i cookie del browser
 *
 * Questa funzione è utilizzata durante il logout per garantire
 * la pulizia completa dello stato dell'applicazione.
 *
 * @example
 * ```typescript
 * import { cleanAllCookies } from './utils/cookies';
 *
 * const handleLogout = () => {
 *   cleanAllCookies();
 *   signOut();
 * };
 * ```
 */
export const cleanAllCookies = (preserve: string[] = []): void => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = (eqPos > -1 ? cookie.substr(0, eqPos) : cookie).trim();
    if (preserve.includes(name)) continue;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
};

/**
 * Rimuove un cookie specifico per nome
 *
 * @param cookieName - Il nome del cookie da rimuovere
 * @param path - Il path del cookie (default: "/")
 * @param domain - Il dominio del cookie (opzionale)
 *
 * @example
 * ```typescript
 * import { removeCookie } from './utils/cookies';
 *
 * removeCookie('sessionToken');
 * removeCookie('userPreference', '/admin');
 * ```
 */
export const removeCookie = (
  cookieName: string,
  path: string = "/",
  domain?: string
): void => {
  let cookieString = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=${path}`;

  if (domain) {
    cookieString += `;domain=${domain}`;
  }

  document.cookie = cookieString;
};

/**
 * Ottiene il valore di un cookie per nome
 *
 * @param cookieName - Il nome del cookie
 * @returns Il valore del cookie o null se non trovato
 *
 * @example
 * ```typescript
 * import { getCookie } from './utils/cookies';
 *
 * const sessionToken = getCookie('sessionToken');
 * if (sessionToken) {
 *   // Usa il token...
 * }
 * ```
 */
export const getCookie = (cookieName: string): string | null => {
  const name = cookieName + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(";");

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return null;
};

/**
 * Imposta un cookie
 *
 * @param cookieName - Il nome del cookie
 * @param cookieValue - Il valore del cookie
 * @param expiryDays - Giorni di scadenza (default: 7)
 * @param path - Il path del cookie (default: "/")
 * @param domain - Il dominio del cookie (opzionale)
 * @param secure - Se il cookie deve essere sicuro (default: false)
 * @param sameSite - Policy SameSite (default: "Lax")
 *
 * @example
 * ```typescript
 * import { setCookie } from './utils/cookies';
 *
 * setCookie('userPreference', 'dark-mode', 30);
 * setCookie('sessionFlag', 'active', 1, '/', undefined, true, 'Strict');
 * ```
 */
export const setCookie = (
  cookieName: string,
  cookieValue: string,
  expiryDays: number = 7,
  path: string = "/",
  domain?: string,
  secure: boolean = false,
  sameSite: "Strict" | "Lax" | "None" = "Lax"
): void => {
  const date = new Date();
  date.setTime(date.getTime() + (expiryDays * 24 * 60 * 60 * 1000));

  let cookieString = `${cookieName}=${cookieValue};expires=${date.toUTCString()};path=${path};SameSite=${sameSite}`;

  if (domain) {
    cookieString += `;domain=${domain}`;
  }

  if (secure) {
    cookieString += ";secure";
  }

  document.cookie = cookieString;
};
